import { Device, Geotab, Group, GroupFilterCondition, TripSearch, User } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import sortBy from "lodash/sortBy";
import type { O } from "ts-toolbelt";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createGroupFilter from "@/utilities/createGroupFilter";

interface Args {
  users?: User[];
  devices?: Device[];
  toDate: Date;
  fromDate: Date;
  limit: number;
}

export function useTripsByDateRange({ users, devices, toDate, fromDate, limit }: Args) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  return useQuery({
    // The group filter decides which devices are searched, so it belongs in the key — otherwise
    // switching groups served the trips of the previously selected group. Only the group ids go
    // in: React Query re-hashes the key by value on every render, and full group records carry
    // far more than identity.
    queryKey: [
      "trips",
      activeSessionId,
      {
        users,
        devices,
        toDate,
        fromDate,
        limit,
        groupIds: groups?.map(g => g.id),
        groupFilterCondition,
      },
    ],

    queryFn: ({ signal }) => {
      if (users && users.length > 0) {
        return findAll(
          geotabClient,
          toDate,
          fromDate,
          users.map(u => ({ userSearch: { id: u.id } })),
          signal,
        );
      }

      if (devices && devices.length > 0) {
        return findAll(
          geotabClient,
          toDate,
          fromDate,
          devices.map(d => ({ deviceSearch: { id: d.id } })),
          signal,
        );
      }
      if (!groups && !groupFilterCondition) {
        return findOne(geotabClient, toDate, fromDate, {}, signal)
          .then(results => sortBy(results, t => t.start))
          .then(results => results.slice(0, 1000));
      }
      return getDeviceByGroupFilter(geotabClient, groups, groupFilterCondition, signal).then(
        devices =>
          findAll(
            geotabClient,
            toDate,
            fromDate,
            devices.map(d => ({ deviceSearch: { id: d.id } })),
            signal,
          ),
      );
    },
  });
}

function getDeviceByGroupFilter(
  geotabClient: Geotab,
  groups?: Group[],
  groupFilterCondition?: GroupFilterCondition,
  signal?: AbortSignal,
) {
  const filter = createGroupFilter(groups, groupFilterCondition, {});
  return geotabClient.get("Device", filter, 1000, signal);
}

function findAll(
  geotabClient: Geotab,
  toDate: Date,
  fromDate: Date,
  search: O.Partial<TripSearch, "deep">[],
  signal?: AbortSignal,
) {
  return Promise.all(search.map(s => findOne(geotabClient, toDate, fromDate, s, signal)))
    .then(results => results.flat())
    .then(results => sortBy(results, t => t.start))
    .then(results => uniqBy(results, t => t.id))
    .then(results => results.slice(0, 1000));
}

function findOne(
  geotabClient: Geotab,
  toDate: Date,
  fromDate: Date,
  search: O.Partial<TripSearch, "deep">,
  signal?: AbortSignal,
) {
  return geotabClient.get(
    "Trip",
    {
      ...search,
      toDate,
      fromDate,
    },
    1000,
    signal,
  );
}
